import * as React from 'react';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import { makeStyles } from '@mui/styles';
import {
  gridColumnDefinitionsSelector,
  useGridSelector,
  useGridApiContext,
  GridPanelContent,
  GridPanelFooter,
  GridPanelHeader,
  GridPanelWrapper,
} from '@mui/x-data-grid-pro';
import { Text, FormControlLabel, Button, Switch } from '../..';

const useStyles = makeStyles((theme: MasonTheme) => ({
  container: {
    padding: 8,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    padding: '0 8px',
    ...theme.typography.h5,
  },
  column: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1px 8px 1px 7px',
  },
  switch: {
    marginRight: 4,
  },
  dragIcon: {
    justifyContent: 'flex-end',
  },
}));

export function ControllerGridColumnsPanel({
  suppressFields = [],
  resetColumnStateKey,
  showHideAll = false,
}: GridColumnsPanelProps) {
  const classes = useStyles();
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);

  const toggleColumn = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const { name } = event.target as HTMLInputElement;
      const column = apiRef.current.getColumn(name);
      apiRef.current.setColumnVisibility(name, !!column.hide);
    },
    [apiRef]
  );

  const currentColumns = React.useMemo(
    // always suppress the checkbox selection column from columns panel
    () => columns.filter((column) => !suppressFields.concat(['__check__']).includes(column.field)),
    [columns, suppressFields]
  );

  const toggleAllColumns = React.useCallback(
    (value: boolean) => {
      apiRef.current.updateColumns(
        currentColumns.map((col) => {
          col.hide = value;
          return col;
        })
      );
    },
    [apiRef, currentColumns]
  );
  const showAllColumns = React.useCallback(() => toggleAllColumns(false), [toggleAllColumns]);
  const hideAllColumns = React.useCallback(() => toggleAllColumns(true), [toggleAllColumns]);
  const handleResetColumnState = () => {
    if (resetColumnStateKey) {
      localStorage?.removeItem(resetColumnStateKey);
      location.reload();
      apiRef.current.forceUpdate();
    }
  };

  return (
    <GridPanelWrapper>
      <GridPanelHeader className={classes.header}>
        <Text className={classes.title} component='span'>
          Show/Hide Columns
        </Text>
      </GridPanelHeader>
      <GridPanelContent>
        <div className={classes.container}>
          {currentColumns.map((column) => (
            <div key={column.field} className={classes.column}>
              <FormControlLabel
                control={
                  <Switch
                    className={classes.switch}
                    checked={!column.hide}
                    onClick={toggleColumn}
                    name={column.field}
                    color='primary'
                    size='small'
                  />
                }
                label={column.headerName || column.field}
                labelPlacement='start'
                sx={{
                  width: `100%`,
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    flexGrow: 1,
                  },
                }}
              />
              {/* TODO: make fields draggable to reorder */}
              {/* {!rootProps.disableColumnReorder && (
                <IconButton
                  className={classes.dragIcon}
                  draggable
                  aria-label={apiRef.current.getLocaleText('columnsPanelDragIconLabel')}
                  title={apiRef.current.getLocaleText('columnsPanelDragIconLabel')}
                  size='small'
                  disabled
                >
                  <GridDragIcon />
                </IconButton>
              )} */}
            </div>
          ))}
        </div>
      </GridPanelContent>
      <GridPanelFooter>
        <div>
          {showHideAll && (
            <Button onClick={hideAllColumns} color='primary'>
              {apiRef.current.getLocaleText('columnsPanelHideAllButton')}
            </Button>
          )}
          <Button onClick={showAllColumns} color='primary'>
            {apiRef.current.getLocaleText('columnsPanelShowAllButton')}
          </Button>
        </div>
        {resetColumnStateKey && (
          <Button color='primary' onClick={handleResetColumnState}>
            Reset
          </Button>
        )}
      </GridPanelFooter>
    </GridPanelWrapper>
  );
}

interface GridColumnsPanelProps {
  suppressFields?: string[];
  resetColumnStateKey?: string;
  showHideAll: boolean;
  onResetColumnState?: () => void;
}
